<template>
<v-dialog :value="value" width="850" persistent>
  <v-card color="fill">
    <v-card-title :class="title">
      <span>Mass Move Ad Features</span>
      <v-col cols="auto" class="ml-auto my-auto px-0 pt-0">
        <span class="text-body-1">
          Selected Features: ({{ featureIds.length }})
        </span>
      </v-col>
    </v-card-title>
    <v-card-subtitle class="pb-0 mb-0">Select a destination ad</v-card-subtitle>
    <v-container class="ma-0 pa-0 pt-4" fluid>
      <v-data-table
        :headers="headers"
        :items="moveDestinations"
        :loading="loading"
        dense
        height="384px"
        fixed-header
        group-by="id"
        item-key="item_key"
        hide-default-footer
        :footer-props="{'items-per-page-options': [-1]}"
        :items-per-page="-1">
        <template #[`group.header`]="{ group, items, headers }">
          <td :colspan="headers.length">
            <v-row align="center">
              <v-col cols="auto">
                <v-checkbox
                  v-model="selectedAds"
                  color="primary"
                  class="my-auto mx-0"
                  hide-details
                  :disabled="moveInProgress || (selectedAds.length > 0 && !selectedAds.includes(group))"
                  :value="group"
                  :ripple="false">
                </v-checkbox>
              </v-col>
              <v-col class="py-4">
                <div>
                  <b>Destination Ad:</b> {{ items[0].ad_name }}
                </div>
                <span class="caption">
                  {{ $dateConfig.formatDate(items[0].start_date) }} - {{ $dateConfig.formatDate(items[0].end_date) }}
                </span>
              </v-col>
            </v-row>
          </td>
        </template>
        <template v-slot:[`item.dest_pig_ids`]="{ item }">
          <span v-if="item && item.dest_pig_ids.some(pig_id => pig_id !== null)">
            {{ item.dest_pig_ids.length }}
          </span>
        </template>
        <template v-slot:[`item.dest_feature_text`]="{ item }">
          <span v-if="item && item.dest_feature_id">
            <span v-if="item.dest_feature_text === ''">
              <em style="color: #aaa">(empty)</em>
            </span>
            <span v-else>
              {{  item.dest_feature_text }}
            </span>
          </span>
        </template>
      </v-data-table>
      <v-divider />
    </v-container>

    <v-toolbar flat color="fill">
      <v-col cols="auto">
        <span v-if="noValidAds" class="warning--text">
          <v-icon color="warning">mdi-alert-circle-outline</v-icon>
          No valid feature destinations found
        </span>
      </v-col>
      <v-spacer />
      <v-btn 
        :disabled="moveInProgress"
        text 
        :class="body" 
        @click="$emit('update:value', false)">
        Cancel
      </v-btn>
      <v-btn 
        :disabled="moveDisabled" 
        :loading="moveInProgress"
        color="warning"
        :class="body" 
        class="white--text ml-2" 
        @click.stop="openConfirmDialog">
        Move
      </v-btn>
    </v-toolbar>
  </v-card>
  <FeaturePromoWarnings
    v-if="warningsModal" 
    v-model="warningsModal" 
    :multiple="false"
    :selectedAd="selectedAd" 
    :gappedPromos="gappedPromos"
    :move="true"
    @cancel="closeWarningsModal" 
    @confirmAction="proceed">
  </FeaturePromoWarnings>
  <MassCopyConfirm
    v-if="confirmDialog" 
    v-model="confirmDialog"
    :move="true"
    @cancel="closeConfirmDialog" 
    @confirmAction="confirmAction">
  </MassCopyConfirm>
</v-dialog>
</template>
<script>
import { utils } from '@/mixins/utils'
import { displayHelpers } from '@/mixins/display-helpers'
import { notification } from '@/mixins/notification'
import { format } from '@/mixins/format'
import { displayAlert } from '@/mixins/alert'
const MassCopyConfirm = () => import('@/components/features/MassCopyConfirm.vue')
import Ads from '@/axios/ad-endpoints'
import Features from '@/axios/feature-endpoints'
import FeaturePromoWarnings from './promotions/FeaturePromoWarnings.vue'
export default {
  name: 'FeatureMassMove',
  mixins: [displayHelpers, format, notification, displayAlert, utils],
  components: { MassCopyConfirm, FeaturePromoWarnings },
  data() {
    return {
      confirmDialog: false,
      adFilter: null,
      moveDestinations: [],
      selectedAds: [],
      warningsModal: false,
      gappedPromos: [],
      loadingAds: false,
      moveInProgress: false,
      valid: true,
      noValidAds: false,
      headerProps: {
        class: 'accent white--text',
        sortable: false
      },
      baseHeaders: [
        {},
        { text: 'Feature Position', value: 'source_feature_position' },
        { text: 'Feature Text', value: 'source_feature_text' },
        { text: 'Promo Item Groups', value: 'dest_pig_ids' },
        { text: 'Non-Promo Items', value: 'non_promo_item_count' },
        { text: 'Destination Ad Feature', value: 'dest_feature_text' },
      ]
    }
  },
  props: {
    value: Boolean,
    selectedAd: Object, 
    selectedFeatures: Array,
    pages: Array,
    currentPage: Object,
    adParty: Object
  },
  async created() {
    await this.getMassMoveDestinations()
  },
  computed: {
    headers() {
      return this.baseHeaders.map(header => {
        return { ...this.headerProps, ...header }
      })
    },
    moveDisabled () {
      return this.loading || (!this.destinationAd?.id)
    },
    loading () {
      return this.loadingAds || this.moveInProgress
    },
    featureIds () {
      if (this.selectedFeatures.length > 0) {
        return this.selectedFeatures.map(feature => feature.id)
      }
      return []
    },
    uniqueAds () {
      if (this.moveDestinations.length > 0) {
        const grouped = Object.groupBy(this.moveDestinations, (dest) => dest.id)
        return Object.keys(grouped).map(adId => {
          const adFeatures = grouped[adId]
          const { 
              id, ad_name, ad_group_id, ad_group_name,
              start_date, end_date, item_source 
          } = adFeatures[0]
          return { 
            id, ad_name, ad_group_id, ad_group_name,
            start_date, end_date, item_source,
            eligible_features: adFeatures,
            feature_count: adFeatures.length
          }
        })
      }
      return []
    },
    destinationAd () {
      return this.selectedAds.length > 0 
        ? this.uniqueAds.find(a => a.id === this.selectedAds[0]) 
        : null
    }
  },
  methods: {
    async getMassMoveDestinations () {
      this.loadingAds = true
      let moveDestinations = []
      try {
        const { data } = await Ads.getValidMassMoveDestinations(this.selectedAd.id, this.featureIds, this.nonPromoOnly)
        if (data?.length > 0) {
          moveDestinations = data.map(item => {
            return {
              item_key: `${item.id}_${item.source_feature_id}`, // v-data-table needs a unique key
              ...item
            }
          })
        }
        this.moveDestinations = moveDestinations
        if (this.moveDestinations.length === 0) {
          this.noValidAds = true
        }
      } catch (e) {
        this.handleError(e)
      } finally {
        this.loadingAds = false
      }
    },
    async massMove () {
      this.moveInProgress = true
      try {
        const destAd = this.destinationAd
        const featureIds = destAd.eligible_features.map(ef => ef.source_feature_id)
        const destFeatureIds = destAd.eligible_features.map(ef => ef.dest_feature_id)
        const massMovePayload = {
          item_source: destAd.item_source,
          ad_id: this.selectedAd.id,
          dest_ad_id: destAd.id,
          start_date: destAd.start_date,
          end_date: destAd.end_date,
          ad_group_id: this.adParty.id,
          feature_ids: featureIds,
          dest_feature_ids: destFeatureIds
        }
        await Features.massMoveFeatures(massMovePayload)
        this.$store.dispatch('setSnackbar', { status: 'success', text: 'Feature(s) moved successfully' })
        this.$emit('closeMassMove')
      } catch (err) {
        this.handleError(err)
      } finally {
        this.moveInProgress = false
      }
    },
    confirmAction () {
      this.closeConfirmDialog()
      this.massMove()
    },
    async openConfirmDialog () {
      await this.checkPromoGap()
      if (this.gappedPromos.length > 0) {
        return this.warningsModal = true
      }
      this.confirmDialog = true
    },
    closeConfirmDialog () {
      this.confirmDialog = false
    },
    closeWarningsModal () {
      this.warningsModal = false
    },
    proceed() {
      this.warningsModal = false
      this.confirmDialog = true
    },
    async checkPromoGap () {
      this.gappedPromos = []
      try {
        const params = {
          source_ad_id: this.selectedAd.id,
          start_date: this.destinationAd.start_date,
          end_date: this.destinationAd.end_date
        }
        const { data } = await this.$Ads.checkAdPromoGap(params)
        if (data?.length > 0) {
          const applicableResults = data.filter(r => this.selectedFeatures.some(f => r.feature_ids.includes(f.id)))
          this.gappedPromos = applicableResults
        }
      } catch (err) {
        this.handleError(err)
      }
    }
  }
}
</script>